/**
 * Base API configuration and utilities
 */

export interface ApiResponse<T> {
    data: T;
    success: boolean;
    error?: {
        code: string;
        message: string;
        details?: Record<string, any>;
    };
}

export class ApiError extends Error {
    public readonly code: string;
    public readonly details?: Record<string, any>;

    constructor(code: string, message: string, details?: Record<string, any>) {
        super(message);
        this.name = 'ApiError';
        this.code = code;
        this.details = details;
    }
}

/**
 * Base API configuration
 */
export const API_CONFIG = {
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001',
    timeout: 10000, // 10 seconds
    headers: {
        'Content-Type': 'application/json',
    },
};

/**
 * Generic API request function with error handling
 */
export async function apiRequest<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<T> {
    const url = `${API_CONFIG.baseURL}${endpoint}`;

    const config: RequestInit = {
        ...options,
        headers: {
            ...API_CONFIG.headers,
            ...options.headers,
        },
    };

    // Add timeout using AbortController
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);
    config.signal = controller.signal;

    try {
        const response = await fetch(url, config);
        clearTimeout(timeoutId);

        if (!response.ok) {
            // Try to parse error response
            let errorData;
            try {
                errorData = await response.json();
            } catch {
                errorData = { message: response.statusText };
            }

            throw new ApiError(
                errorData.error?.code || `HTTP_${response.status}`,
                errorData.error?.message || errorData.message || `Request failed with status ${response.status}`,
                errorData.error?.details
            );
        }

        // Handle empty responses
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            return {} as T;
        }

        const data = await response.json();
        return data;
    } catch (error) {
        clearTimeout(timeoutId);

        if (error instanceof ApiError) {
            throw error;
        }

        if (error instanceof Error) {
            if (error.name === 'AbortError') {
                throw new ApiError('TIMEOUT', 'Request timed out');
            }

            if (error.message.includes('Failed to fetch')) {
                throw new ApiError('NETWORK_ERROR', 'Network error - please check your connection');
            }
        }

        throw new ApiError('UNKNOWN_ERROR', 'An unexpected error occurred');
    }
}

/**
 * Build query string from parameters object
 */
export function buildQueryString(params: Record<string, any>): string {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
                // Handle array parameters (e.g., categories)
                value.forEach(item => searchParams.append(key, item.toString()));
            } else {
                searchParams.set(key, value.toString());
            }
        }
    });

    const queryString = searchParams.toString();
    return queryString ? `?${queryString}` : '';
}